import Component from '@glimmer/component';
import { t } from 'ember-intl';
import AlertBanner from 'tio-reimagined/components/alert-banner';

type ApplicationState = string;
type AlertIntent = 'info' | 'success' | 'warning' | 'tip';

interface PhaseConfig {
  intent: AlertIntent;
  titleKey: string;
  messageKey: string;
}

interface PhaseExplainerSignature {
  Args: {
    /** Current application state string (e.g., 'TAS.ApplicationState.DEFINE_COURSES') */
    state: ApplicationState;
  };
  Element: HTMLDivElement;
}

const PHASE_CONFIG: Record<string, PhaseConfig> = {
  'TAS.ApplicationState.DEFINE_COURSES': {
    intent: 'info',
    titleKey: 'tuition_assistance.reimagined.wizard.phase.define_courses_title',
    messageKey: 'tuition_assistance.reimagined.wizard.phase.define_courses_message',
  },
  'TAS.ApplicationState.PENDING_COURSES_APPROVAL': {
    intent: 'info',
    titleKey: 'tuition_assistance.reimagined.wizard.phase.pending_approval_title',
    messageKey: 'tuition_assistance.reimagined.wizard.phase.pending_approval_message',
  },
  'TAS.ApplicationState.COURSES_APPROVED': {
    intent: 'success',
    titleKey: 'tuition_assistance.reimagined.wizard.phase.courses_approved_title',
    messageKey: 'tuition_assistance.reimagined.wizard.phase.courses_approved_message',
  },
  'TAS.ApplicationState.ATTEND': {
    intent: 'success',
    titleKey: 'tuition_assistance.reimagined.wizard.phase.attend_title',
    messageKey: 'tuition_assistance.reimagined.wizard.phase.attend_message',
  },
  'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL': {
    intent: 'info',
    titleKey: 'tuition_assistance.reimagined.wizard.phase.pending_evidence_title',
    messageKey: 'tuition_assistance.reimagined.wizard.phase.pending_evidence_message',
  },
  'TAS.ApplicationState.EVIDENCE_APPROVED': {
    intent: 'success',
    titleKey: 'tuition_assistance.reimagined.wizard.phase.evidence_approved_title',
    messageKey: 'tuition_assistance.reimagined.wizard.phase.evidence_approved_message',
  },
  'TAS.ApplicationState.EVIDENCE_NOT_APPROVED': {
    intent: 'warning',
    titleKey: 'tuition_assistance.reimagined.wizard.phase.evidence_rejected_title',
    messageKey: 'tuition_assistance.reimagined.wizard.phase.evidence_rejected_message',
  },
  'TAS.ApplicationState.COURSES_NOT_APPROVED': {
    intent: 'warning',
    titleKey: 'tuition_assistance.reimagined.wizard.phase.courses_rejected_title',
    messageKey: 'tuition_assistance.reimagined.wizard.phase.courses_rejected_message',
  },
  'TAS.ApplicationState.FULFILLED': {
    intent: 'success',
    titleKey: 'tuition_assistance.reimagined.wizard.phase.fulfilled_title',
    messageKey: 'tuition_assistance.reimagined.wizard.phase.fulfilled_message',
  },
};

/**
 * PhaseExplainer - Contextual messaging about the two-phase TA process
 *
 * Renders state-appropriate guidance so users always understand where
 * they are in the pre-approval vs evidence submission lifecycle.
 *
 * @example
 * ```gts
 * <PhaseExplainer @state={{this.application.state}} />
 * ```
 */
export default class PhaseExplainer extends Component<PhaseExplainerSignature> {
  get config(): PhaseConfig | undefined {
    return PHASE_CONFIG[this.args.state];
  }

  <template>
    {{#if this.config}}
      <AlertBanner @intent={{this.config.intent}} @title={{t this.config.titleKey}} ...attributes>
        {{t this.config.messageKey}}
      </AlertBanner>
    {{/if}}
  </template>
}
